import React from "react";
import { useHistory, useParams } from "react-router-dom";
import MerchantStatusBadge from "../components/MerchantStatusBadge";
import { MerchantBackIcon } from "../components/MerchantIcons";
import {
  fetchMerchantTransactionDetail,
  getMerchantApiErrorMessage,
  normalizeMerchantApiTransaction,
} from "../data/merchantApi";
import {
  getMerchantCountryCode,
  getMerchantMethod,
  getMerchantStatus,
  getMerchantStatusLabel,
  pickMerchantField,
  toMerchantString,
} from "../data/merchantLegacyUtils";
import { formatMerchantCurrency } from "../data/merchantData";

function formatOutletDisplayName(name) {
  return toMerchantString(name).replace("@", " · ");
}

function buildDetailSections(transaction) {
  return [
    {
      title: "Transaction Overview",
      rows: [
        [
          "Outlet",
          formatOutletDisplayName(
            toMerchantString(pickMerchantField(transaction, ["storeName"]), "Unknown Store"),
          ),
        ],
        ["Method", getMerchantMethod(transaction)],
        ["Scheme", toMerchantString(pickMerchantField(transaction, ["cardPayWay", "scheme"]), "—")],
        ["Issuer Country", getMerchantCountryCode(transaction)],
      ],
    },
    {
      title: "Amounts",
      rows: [
        ["Gross Amount", formatMerchantCurrency(pickMerchantField(transaction, ["amount"], 0))],
        ["Net Amount", formatMerchantCurrency(pickMerchantField(transaction, ["netAmount", "amount"], 0), 2)],
        ["Refundable Amount", formatMerchantCurrency(pickMerchantField(transaction, ["refundableAmount"], 0), 2)],
        ["Service Charge", formatMerchantCurrency(pickMerchantField(transaction, ["serviceCharge"], 0), 2)],
      ],
    },
    {
      title: "Identifiers",
      rows: [
        ["Transaction ID", toMerchantString(pickMerchantField(transaction, ["id", "txnId"]), "—"), "merchant-mono"],
        ["Trade Number", toMerchantString(pickMerchantField(transaction, ["tradeNumber"]), "—"), "merchant-mono"],
        ["Acquirer ID", toMerchantString(pickMerchantField(transaction, ["acquirerTransactionId"]), "—"), "merchant-mono"],
        ["Client ID", toMerchantString(pickMerchantField(transaction, ["clientTransactionId"]), "—"), "merchant-mono"],
        ["Terminal ID", toMerchantString(pickMerchantField(transaction, ["terminalId"]), "—"), "merchant-mono"],
        ["Terminal STAN", toMerchantString(pickMerchantField(transaction, ["terminalStan"]), "—"), "merchant-mono"],
      ],
    },
    {
      title: "Timeline",
      rows: [
        ["Captured Time", toMerchantString(pickMerchantField(transaction, ["capturedTime", "creationTime", "createdAt"]), "—")],
        ["Created Time", toMerchantString(pickMerchantField(transaction, ["creationTime", "capturedTime", "createdAt"]), "—")],
        ["Status", getMerchantStatusLabel(getMerchantStatus(transaction))],
      ],
    },
  ];
}

export default function MerchantTransactionDetailPage() {
  const history = useHistory();
  const { transactionId } = useParams();
  const [transaction, setTransaction] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [requestState, setRequestState] = React.useState("loading");

  React.useEffect(
    function loadTransactionDetail() {
      let isActive = true;

      async function requestTransactionDetail() {
        setIsLoading(true);
        setRequestState("loading");
        setErrorMessage("");

        try {
          const response = await fetchMerchantTransactionDetail(transactionId);

          if (!isActive) {
            return;
          }

          setTransaction(normalizeMerchantApiTransaction(response));
          setRequestState("success");
        } catch (error) {
          if (!isActive) {
            return;
          }

          setTransaction(null);
          setRequestState("error");
          setErrorMessage(
            getMerchantApiErrorMessage(
              error,
              "This transaction could not be loaded from the merchant API.",
            ),
          );
        } finally {
          if (isActive) {
            setIsLoading(false);
          }
        }
      }

      requestTransactionDetail();

      return function cleanup() {
        isActive = false;
      };
    },
    [transactionId],
  );

  if (isLoading) {
    return (
      <div className="merchant-card merchant-empty-state">
        <div className="merchant-page-title">Loading transaction</div>
        <div className="merchant-page-subtitle">
          Fetching the latest detail from the merchant API.
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="merchant-card merchant-empty-state">
        <div className="merchant-page-title">Transaction not found</div>
        <div className="merchant-page-subtitle">{errorMessage || "This transaction could not be found."}</div>
        <button
          className="merchant-button merchant-button-primary"
          type="button"
          onClick={function goBackToList() {
            history.push("/transactions");
          }}
        >
          Back to transactions
        </button>
      </div>
    );
  }

  const detailSections = buildDetailSections(transaction);
  const transactionStatus = getMerchantStatus(transaction);
  const outletName = formatOutletDisplayName(
    toMerchantString(
      pickMerchantField(transaction, ["storeName"]),
      "Unknown Store",
    ),
  );
  const methodLabel = getMerchantMethod(transaction);
  const schemeLabel = toMerchantString(
    pickMerchantField(transaction, ["cardPayWay", "scheme"]),
    "—",
  );
  const issuerCountry = getMerchantCountryCode(transaction);
  const capturedTime = toMerchantString(
    pickMerchantField(transaction, ["capturedTime", "creationTime", "createdAt"]),
    "—",
  );
  const transactionIdentifier = toMerchantString(
    pickMerchantField(transaction, ["id", "txnId"]),
    "—",
  );
  const grossAmount = formatMerchantCurrency(pickMerchantField(transaction, ["amount"], 0), 2);
  const netAmount = formatMerchantCurrency(
    pickMerchantField(transaction, ["netAmount", "amount"], 0),
    2,
  );
  const refundableAmount = formatMerchantCurrency(
    pickMerchantField(transaction, ["refundableAmount"], 0),
    2,
  );

  return (
    <div className="merchant-page merchant-transaction-detail-page">
      <div className="merchant-page-header merchant-page-header-row">
        <div>
          <button
            className="merchant-transaction-back"
            type="button"
            onClick={function handleBack() {
              history.push("/transactions");
            }}
          >
            <span className="merchant-transaction-back-icon">
              <MerchantBackIcon />
            </span>
            <span>Back to transactions</span>
          </button>
          <div className="merchant-page-title">Transaction Details</div>
          <div className="merchant-page-subtitle">
            {outletName} · {capturedTime}
          </div>
        </div>
        <div className="merchant-page-actions">
          <MerchantStatusBadge
            status={transactionStatus}
            label={getMerchantStatusLabel(transactionStatus)}
          />
          <span className="merchant-table-chip">{methodLabel}</span>
          {schemeLabel !== "—" ? <span className="merchant-table-chip">{schemeLabel}</span> : null}
        </div>
      </div>

      {errorMessage ? (
        <div className="merchant-data-note">
          {errorMessage}
          {requestState === "loading" ? (
            <div className="merchant-inline-status merchant-inline-status-loading">
              <span className="merchant-request-spinner" />
              Transaction request in progress
            </div>
          ) : null}
          {requestState === "error" ? (
            <div className="merchant-inline-status merchant-inline-status-error">
              Request failed
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="merchant-card merchant-transaction-summary-card">
        <div className="merchant-transaction-summary-main">
          <div>
            <div className="merchant-transaction-summary-label">Amount</div>
            <div className="merchant-transaction-summary-amount">{grossAmount}</div>
            <div className="merchant-transaction-summary-subtitle">
              Gross amount captured for this transaction
            </div>
          </div>
          <div className="merchant-transaction-summary-meta">
            <span className="merchant-table-chip merchant-mono">{transactionIdentifier}</span>
          </div>
        </div>
        <div className="merchant-transaction-summary-stats">
          <div className="merchant-transaction-summary-stat">
            <div className="merchant-transaction-summary-stat-label">Net Amount</div>
            <div className="merchant-transaction-summary-stat-value">{netAmount}</div>
          </div>
          <div className="merchant-transaction-summary-stat">
            <div className="merchant-transaction-summary-stat-label">Refundable</div>
            <div className="merchant-transaction-summary-stat-value">{refundableAmount}</div>
          </div>
          <div className="merchant-transaction-summary-stat">
            <div className="merchant-transaction-summary-stat-label">Issuer Country</div>
            <div className="merchant-transaction-summary-stat-value">{issuerCountry}</div>
          </div>
          <div className="merchant-transaction-summary-stat">
            <div className="merchant-transaction-summary-stat-label">Outlet</div>
            <div className="merchant-transaction-summary-stat-value">{outletName}</div>
          </div>
        </div>
      </div>

      <div className="merchant-transaction-detail-sections">
        {detailSections.map(function renderSection(section) {
          return (
            <section className="merchant-card merchant-transaction-detail-card" key={section.title}>
              <div className="merchant-settings-title">{section.title}</div>
              <div className="merchant-transaction-detail-rows">
                {section.rows.map(function renderRow(row) {
                  return (
                    <div className="merchant-settings-row merchant-transaction-detail-row" key={row[0]}>
                      <span className="merchant-settings-key">{row[0]}</span>
                      <span
                        className={
                          "merchant-settings-value merchant-transaction-detail-value" +
                          (row[2] === "merchant-mono" ? " merchant-mono" : "")
                        }
                      >
                        {row[1]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
